import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconArrowLeft, IconCalendar, IconUsers, IconClipboardHeart, IconLibrary,
  IconMessages, IconBuildingStore, IconCoins, IconPlus, IconWand, IconExternalLink,
  IconCheck, IconTrash,
} from "@tabler/icons-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";

const TABS = [
  { id: "agenda", es: "Agenda", en: "Agenda", icon: <IconCalendar size={13} /> },
  { id: "pacientes", es: "Pacientes", en: "Patients", icon: <IconUsers size={13} /> },
  { id: "tests", es: "Tests", en: "Tests", icon: <IconLibrary size={13} /> },
  { id: "psi", es: "Psi-Consulta", en: "Psi-Consult", icon: <IconMessages size={13} /> },
  { id: "clinica", es: "Mi Clínica", en: "My Clinic", icon: <IconBuildingStore size={13} /> },
  { id: "finanzas", es: "Finanzas", en: "Finance", icon: <IconCoins size={13} /> },
];

export default function SintoniaHome() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [tab, setTab] = useState("agenda");
  const [pendingCount, setPendingCount] = useState(0);

  // Contador de citas booked (source=public → esperando confirmación)
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/sintonia/appointments?status=booked&limit=200");
        if (alive) setPendingCount(Array.isArray(data) ? data.length : 0);
      } catch { /* silent */ }
    };
    load();
    const iv = setInterval(load, 60000); // refresh cada 60s
    return () => { alive = false; clearInterval(iv); };
  }, [tab]); // re-count al cambiar de tab (por si el clínico confirmó una)

  return (
    <>
      <div className="subbar">
        <button className="btn-ghost btn text-[12px] px-2 py-1" onClick={() => nav("/app")} data-testid="sintonia-back">
          <IconArrowLeft size={13} /> {t("app.back")}
        </button>
        <span className="t">MÓDULO SINTONÍA</span>
      </div>
      <div className="wrap-app max-w-[1080px] py-10">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <p className="eyebrow">{lang === "es" ? "Consultorio digital" : "Digital clinic"}</p>
            <h1 className="font-display font-medium text-[clamp(24px,3vw,32px)] mt-1">
              {lang === "es" ? "Sintonía · Tu consultorio" : "Sintonía · Your clinic"}
            </h1>
          </div>
          {pendingCount > 0 && (
            <button
              type="button"
              onClick={() => setTab("agenda")}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-honey bg-warnbg text-warntext text-[12.5px] font-medium hover:bg-honey/20 transition"
              data-testid="sintonia-pending-badge"
            >
              <span className="w-2 h-2 rounded-full bg-honey animate-pulse" />
              {pendingCount} {lang === "es"
                ? (pendingCount === 1 ? "cita pendiente de confirmar" : "citas pendientes de confirmar")
                : (pendingCount === 1 ? "booking to confirm" : "bookings to confirm")}
            </button>
          )}
        </div>
        <div className="border-b border-line flex gap-1 overflow-x-auto">
          {TABS.map((tt) => (
            <button
              key={tt.id}
              onClick={() => setTab(tt.id)}
              className={`px-3 py-2 text-[12.5px] font-medium transition border-b-2 -mb-px flex items-center gap-1.5 ${tab === tt.id ? "border-teal text-teal-deep" : "border-transparent text-muted hover:text-teal-deep"}`}
              data-testid={`sintonia-tab-${tt.id}`}
            >
              {tt.icon} {lang === "es" ? tt.es : tt.en}
            </button>
          ))}
        </div>
        <div className="mt-6">
          {tab === "agenda" && <TabAgenda />}
          {tab === "pacientes" && <TabPacientes />}
          {tab === "tests" && <TabTests />}
          {tab === "psi" && <TabPsi />}
          {tab === "clinica" && <TabClinica />}
          {tab === "finanzas" && <TabFinanzas />}
        </div>
      </div>
    </>
  );
}

// -------------- Agenda --------------
function TabAgenda() {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ patient_name: "", patient_email: "", service_id: "", start: "", notes: "" });
  const [error, setError] = useState("");

  const load = async () => {
    const [a, s] = await Promise.all([
      api.get("/sintonia/appointments"),
      api.get("/sintonia/settings"),
    ]);
    setItems(a.data);
    setSettings(s.data);
    if (!form.service_id && s.data?.services?.length) {
      setForm((f) => ({ ...f, service_id: s.data.services[0].id }));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    setError("");
    try {
      await api.post("/sintonia/appointments", { ...form, start: new Date(form.start).toISOString() });
      setShowNew(false);
      setForm({ patient_name: "", patient_email: "", service_id: settings?.services?.[0]?.id || "", start: "", notes: "" });
      await load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const setStatus = async (id, status) => {
    await api.patch(`/sintonia/appointments/${id}`, { status });
    await load();
  };

  const publicUrl = settings ? `${window.location.origin}/agendar/${settings.slug}` : "";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[13px] text-muted">
          {lang === "es"
            ? "Tus citas ordenadas por fecha. Comparte tu enlace público para que pacientes se agenden solos."
            : "Your appointments. Share your public link for self-booking."}
        </div>
        <div className="flex gap-2">
          {settings && (
            <a href={publicUrl} target="_blank" rel="noreferrer" className="btn-ghost btn text-[12px]" data-testid="sintonia-public-link">
              <IconExternalLink size={13} /> {lang === "es" ? "Enlace público" : "Public link"}
            </a>
          )}
          <button className="btn text-[12px]" onClick={() => setShowNew((v) => !v)} data-testid="sintonia-new-appt">
            <IconPlus size={13} /> {lang === "es" ? "Nueva cita" : "New appointment"}
          </button>
        </div>
      </div>

      {showNew && (
        <div className="card">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="field"><label>{lang === "es" ? "Nombre del paciente" : "Patient name"}</label><input className="input" value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} data-testid="appt-patient-name" /></div>
            <div className="field"><label>Email</label><input className="input" type="email" value={form.patient_email} onChange={(e) => setForm({ ...form, patient_email: e.target.value })} data-testid="appt-patient-email" /></div>
            <div className="field"><label>{lang === "es" ? "Servicio" : "Service"}</label>
              <select className="select-css" value={form.service_id} onChange={(e) => setForm({ ...form, service_id: e.target.value })} data-testid="appt-service">
                {settings?.services?.map((s) => <option key={s.id} value={s.id}>{s.name} · L{s.price_hnl}</option>)}
              </select>
            </div>
            <div className="field"><label>{lang === "es" ? "Inicio" : "Start"}</label><input type="datetime-local" className="input" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="appt-start" /></div>
          </div>
          {error && <p className="text-errtext text-[13px]">{error}</p>}
          <button className="btn" onClick={create} data-testid="appt-create-submit">
            <IconCheck size={13} /> {lang === "es" ? "Crear cita" : "Create"}
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-tert text-[13px]">{lang === "es" ? "Aún no hay citas." : "No appointments yet."}</p>
      ) : (
        <div className="space-y-2">
          {items.map((a) => (
            <div key={a.id} className="card" data-testid={`appt-${a.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-display text-[16px] font-medium">{a.patient_name}</p>
                  <p className="text-tert text-[12px]">
                    {new Date(a.start).toLocaleString(lang === "es" ? "es-HN" : "en-US")} · {a.service_name} · L{a.price_hnl}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`tag ${a.status === "completed" ? "bg-okbg text-oktext" : a.status === "cancelled" ? "bg-errbg text-errtext" : a.status === "booked" ? "bg-warnbg text-warntext" : "bg-teal-soft text-teal-deep"}`}>
                    {a.status}
                  </span>
                  {a.status !== "completed" && (
                    <button className="btn-ghost btn text-[11px]" onClick={() => setStatus(a.id, "completed")} data-testid={`appt-complete-${a.id}`}>
                      <IconCheck size={11} /> {lang === "es" ? "Completar" : "Complete"}
                    </button>
                  )}
                  {a.status !== "cancelled" && (
                    <button className="btn-ghost btn text-[11px]" onClick={() => setStatus(a.id, "cancelled")} data-testid={`appt-cancel-${a.id}`}>
                      {lang === "es" ? "Cancelar" : "Cancel"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// -------------- Pacientes --------------
function TabPacientes() {
  const { lang } = useI18n();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", condition: "", notes: "" });
  const load = async () => { const { data } = await api.get("/sintonia/patients"); setItems(data); };
  useEffect(() => { load(); }, []);
  const create = async () => {
    await api.post("/sintonia/patients", { ...form, email: form.email || null, phone: form.phone || null });
    setShowNew(false); setForm({ name: "", email: "", phone: "", condition: "", notes: "" });
    await load();
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-[13px] text-muted">{lang === "es" ? "Tus pacientes" : "Your patients"}</div>
        <button className="btn text-[12px]" onClick={() => setShowNew((v) => !v)} data-testid="patient-new">
          <IconPlus size={13} /> {lang === "es" ? "Nuevo paciente" : "New patient"}
        </button>
      </div>
      {showNew && (
        <div className="card">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="field"><label>{lang === "es" ? "Nombre" : "Name"}</label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="patient-name" /></div>
            <div className="field"><label>Email</label><input className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="field"><label>{lang === "es" ? "Teléfono" : "Phone"}</label><input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="field"><label>{lang === "es" ? "Motivo/Condición" : "Condition"}</label><input className="input" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} /></div>
          </div>
          <button className="btn" onClick={create} data-testid="patient-create-submit"><IconCheck size={13} /> {lang === "es" ? "Crear" : "Create"}</button>
        </div>
      )}
      <div className="space-y-2">
        {items.map((p) => (
          <button key={p.id} className="w-full card text-left flex items-center justify-between hover:border-teal" onClick={() => nav(`/app/sintonia/paciente/${p.id}`)} data-testid={`patient-open-${p.id}`}>
            <div>
              <p className="font-display text-[16px] font-medium">{p.name}</p>
              <p className="text-tert text-[12px]">{p.email || "—"} · {p.condition || "—"}</p>
            </div>
            <span className="text-teal text-[12px] font-medium">{lang === "es" ? "Expediente" : "File"} →</span>
          </button>
        ))}
        {items.length === 0 && <p className="text-tert text-[13px]">{lang === "es" ? "Sin pacientes." : "No patients."}</p>}
      </div>
    </div>
  );
}

// -------------- Tests --------------
function TabTests() {
  const { lang } = useI18n();
  const [tests, setTests] = useState([]);
  const [patients, setPatients] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [patientId, setPatientId] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => { (async () => {
    const [tt, pp] = await Promise.all([api.get("/sintonia/tests"), api.get("/sintonia/patients")]);
    setTests(tt.data); setPatients(pp.data);
    if (pp.data.length && !patientId) setPatientId(pp.data[0].id);
  })(); /* eslint-disable-next-line */ }, []);

  const openTest = (t) => {
    setSelectedTest(t); setAnswers(Array(t.items.length).fill(0)); setResult(null);
  };
  const apply = async () => {
    const { data } = await api.post("/sintonia/tests/apply", { test_id: selectedTest.id, patient_id: patientId, answers });
    setResult(data);
  };

  if (selectedTest) {
    return (
      <div className="space-y-3">
        <button className="btn-ghost btn text-[12px]" onClick={() => setSelectedTest(null)}>← {lang === "es" ? "Tests" : "Tests"}</button>
        <h3 className="font-display text-[20px] font-medium">{selectedTest.name}</h3>
        <p className="text-muted text-[13px]">{selectedTest.description}</p>
        <div className="field"><label>{lang === "es" ? "Paciente" : "Patient"}</label>
          <select className="select-css" value={patientId} onChange={(e) => setPatientId(e.target.value)} data-testid="test-patient">
            {patients.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        {selectedTest.items.map((it, i) => (
          <div key={i} className="card">
            <p className="text-[14px] mb-2">{i + 1}. {it.q}</p>
            <div className="flex flex-wrap gap-2">
              {selectedTest.scale.map((s) => (
                <button key={s.v} className={`chip ${answers[i] === s.v ? "sel" : ""}`} onClick={() => { const a = [...answers]; a[i] = s.v; setAnswers(a); }} data-testid={`test-a-${i}-${s.v}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        ))}
        <button className="btn" onClick={apply} data-testid="test-apply"><IconCheck size={13} /> {lang === "es" ? "Aplicar" : "Apply"}</button>
        {result && (
          <div className="card border-l-[3px] border-l-teal" data-testid="test-result">
            <p className="eyebrow">{lang === "es" ? "Resultado" : "Result"}</p>
            <p className="font-display text-[22px] font-medium mt-1">{result.score} / {result.max_score} · {result.band}</p>
            <p className="text-[13.5px] mt-2">{result.note}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {tests.map((t) => (
        <div key={t.id} className="mod-card" onClick={() => openTest(t)} data-testid={`test-open-${t.id}`}>
          <span className="tag">{t.name}</span>
          <h4 className="font-display text-[17px] font-medium mt-2">{t.name}</h4>
          <p className="text-muted text-[13px] mt-1">{t.description}</p>
          <p className="text-tert text-[11.5px] mt-2">{t.items.length} {lang === "es" ? "preguntas" : "questions"}</p>
        </div>
      ))}
    </div>
  );
}

// -------------- Psi-Consulta --------------
function TabPsi() {
  const { lang } = useI18n();
  const [docs, setDocs] = useState([]);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const load = async () => { const { data } = await api.get("/sintonia/documents"); setDocs(data); };
  useEffect(() => { load(); }, []);
  const upload = async () => {
    if (!title.trim() || !content.trim()) return;
    await api.post("/sintonia/documents", { title: title.trim(), content: content.trim(), tags: [] });
    setTitle(""); setContent(""); await load();
  };
  const askIt = async () => {
    setError(""); setAnswer(null); setLoading(true);
    try { const { data } = await api.post("/sintonia/consult", { question, doc_ids: null }); setAnswer(data); }
    catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };
  const removeDoc = async (id) => { await api.delete(`/sintonia/documents/${id}`); await load(); };

  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="space-y-3">
        <p className="lbl m-0">{lang === "es" ? "Biblioteca" : "Library"}</p>
        <div className="card">
          <input className="input mb-2" placeholder={lang === "es" ? "Título" : "Title"} value={title} onChange={(e) => setTitle(e.target.value)} data-testid="psi-doc-title" />
          <textarea className="textarea" placeholder={lang === "es" ? "Pega o escribe el contenido..." : "Paste content..."} value={content} onChange={(e) => setContent(e.target.value)} data-testid="psi-doc-content" />
          <button className="btn mt-2" onClick={upload} data-testid="psi-doc-upload"><IconPlus size={12} /> {lang === "es" ? "Guardar" : "Save"}</button>
        </div>
        {docs.map((d) => (
          <div key={d.id} className="card flex items-center justify-between" data-testid={`psi-doc-${d.id}`}>
            <div><p className="font-medium text-[14px]">{d.title}</p><p className="text-tert text-[11.5px]">{d.content.length} caracteres</p></div>
            <button className="btn-ghost btn text-[11px] px-2 py-1" onClick={() => removeDoc(d.id)}><IconTrash size={12} /></button>
          </div>
        ))}
      </div>
      <div className="space-y-3">
        <p className="lbl m-0">{lang === "es" ? "Consulta" : "Consult"}</p>
        <div className="card">
          <textarea className="textarea" placeholder={lang === "es" ? "Pregunta sobre tus documentos..." : "Ask about your documents..."} value={question} onChange={(e) => setQuestion(e.target.value)} data-testid="psi-question" />
          <button className="btn mt-2" onClick={askIt} disabled={loading || !question.trim() || docs.length === 0} data-testid="psi-ask">
            <IconWand size={13} /> {loading ? "..." : (lang === "es" ? "Preguntar al Sistema Inteligente" : "Ask the Smart System")}
          </button>
          {error && <p className="text-errtext text-[13px] mt-2">{error}</p>}
          {answer && (
            <div className="mt-3 pt-3 border-t border-line" data-testid="psi-answer">
              <p className="text-[14px] leading-relaxed whitespace-pre-wrap">{answer.answer}</p>
              <p className="text-tert text-[11.5px] mt-2">
                {lang === "es" ? "Fuentes" : "Sources"}: {answer.sources_used.join(", ")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// -------------- Mi Clínica --------------
function TabClinica() {
  const { lang } = useI18n();
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState(false);
  const load = async () => { const { data } = await api.get("/sintonia/settings"); setS(data); };
  useEffect(() => { load(); }, []);
  const save = async () => {
    setSaved(false);
    await api.patch("/sintonia/settings", s);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };
  if (!s) return <p className="text-tert text-[12px] font-mono uppercase">Cargando…</p>;
  return (
    <div className="space-y-4">
      <div className="card">
        <p className="lbl m-0 mb-3">{lang === "es" ? "Perfil clínico" : "Clinical profile"}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="field"><label>{lang === "es" ? "Nombre" : "Name"}</label><input className="input" value={s.clinician_name} onChange={(e) => setS({ ...s, clinician_name: e.target.value })} data-testid="clinic-name" /></div>
          <div className="field"><label>{lang === "es" ? "Slug público" : "Public slug"}</label><input className="input" value={s.slug} disabled /></div>
          <div className="field sm:col-span-2"><label>Bio</label><textarea className="textarea" value={s.bio || ""} onChange={(e) => setS({ ...s, bio: e.target.value })} data-testid="clinic-bio" /></div>
          <div className="field"><label>{lang === "es" ? "Zona horaria" : "Timezone"}</label><input className="input" value={s.timezone || ""} onChange={(e) => setS({ ...s, timezone: e.target.value })} /></div>
          <div className="field"><label>{lang === "es" ? "Correo" : "Email"}</label><input className="input" type="email" value={s.contact_email || ""} onChange={(e) => setS({ ...s, contact_email: e.target.value })} /></div>
        </div>
      </div>
      <div className="card">
        <p className="lbl m-0 mb-3">{lang === "es" ? "Servicios" : "Services"}</p>
        {s.services.map((svc, i) => (
          <div key={svc.id} className="grid sm:grid-cols-[2fr_1fr_1fr_1fr] gap-2 mb-2">
            <input className="input" value={svc.name} onChange={(e) => { const ns = [...s.services]; ns[i] = { ...ns[i], name: e.target.value }; setS({ ...s, services: ns }); }} />
            <input className="input" type="number" value={svc.duration_min} onChange={(e) => { const ns = [...s.services]; ns[i] = { ...ns[i], duration_min: Number(e.target.value) }; setS({ ...s, services: ns }); }} />
            <input className="input" type="number" value={svc.price_hnl} onChange={(e) => { const ns = [...s.services]; ns[i] = { ...ns[i], price_hnl: Number(e.target.value) }; setS({ ...s, services: ns }); }} />
            <input className="input" type="number" value={svc.price_usd} onChange={(e) => { const ns = [...s.services]; ns[i] = { ...ns[i], price_usd: Number(e.target.value) }; setS({ ...s, services: ns }); }} />
          </div>
        ))}
      </div>
      <button className="btn" onClick={save} data-testid="clinic-save"><IconCheck size={13} /> {lang === "es" ? "Guardar" : "Save"}</button>
      {saved && <span className="text-oktext text-[12px] ml-2">{lang === "es" ? "Guardado" : "Saved"}</span>}
    </div>
  );
}

// -------------- Finanzas --------------
function TabFinanzas() {
  const { lang } = useI18n();
  const [m, setM] = useState(null);
  useEffect(() => { (async () => { const { data } = await api.get("/sintonia/metrics"); setM(data); })(); }, []);
  if (!m) return <p className="text-tert text-[12px] font-mono uppercase">Cargando…</p>;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="kpi"><div className="kl">{lang === "es" ? "Ingresos mes" : "Month revenue"}</div><div className="kv">L{m.revenue_hnl_month.toLocaleString()}</div></div>
        <div className="kpi"><div className="kl">{lang === "es" ? "Por cobrar" : "Due"}</div><div className="kv" style={{ color: "#854f0b" }}>L{m.revenue_due_hnl.toLocaleString()}</div></div>
        <div className="kpi"><div className="kl">{lang === "es" ? "Próximas citas" : "Upcoming"}</div><div className="kv">{m.upcoming_appointments}</div></div>
        <div className="kpi"><div className="kl">{lang === "es" ? "Pacientes" : "Patients"}</div><div className="kv">{m.patients_total}</div></div>
      </div>
      <div className="card text-[13px] text-muted">
        {lang === "es"
          ? "Estos números se calculan sobre citas del mes en curso. Detalles y cobros por cita en la Agenda."
          : "Numbers based on this month's appointments. Detail per appointment in Agenda."}
      </div>
    </div>
  );
}
